import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isObject } from 'lodash';
import { Box, Stack, useTheme } from '@mui/material';
import { formatCurrency } from '@monorepo/client-common';
import { useBreakpoints } from '@monorepo/nova-ui';
import { AccountBalance, Program } from '@monorepo/types';
import { useResponsiveFontSize } from './hooks';
import { Metric } from './metric';
import { MetricSeparator } from './metric-separator';

interface MetricListProps {
  program: Program;
  accountMetadata?: AccountBalance;
  extra?: ReactNode;
}

export const MetricList: FC<MetricListProps> = ({
  program,
  accountMetadata,
  extra,
}) => {
  const { t } = useTranslation('dashboardHeader');
  const theme = useTheme();
  const { isTabletOrMobile } = useBreakpoints();
  const compact = !isObject(accountMetadata);

  const { responsiveFontSizes } = useResponsiveFontSize(compact);

  const valueProps = {
    fontSize: responsiveFontSizes,
    marginRight: {
      xs: theme.spacing(1),
      sm: theme.spacing(10),
    },
  };

  const initialBalance = program?.config?.buyingPower || 0;
  const qualifiedProfit = accountMetadata?.profitAndLoss || 0;
  const target =
    (program?.config?.target || 0) + (program?.config?.buyingPower || 0);

  const metrics = [
    {
      label: t('metrics.initialBalance.label'),
      tooltip: t('metrics.initialBalance.tooltip'),
      value: formatCurrency(initialBalance, {
        currency: 'USD',
        decimalPlaces: isTabletOrMobile ? 0 : 2,
      }),
      numericValue: initialBalance,
      primary: true,
      valueProps,
      compact,
      minWidth: isTabletOrMobile ? 100 : undefined,
    },
    ...(!compact
      ? [
          {
            label: t('metrics.qualifiedProfit.label'),
            tooltip: t('metrics.qualifiedProfit.tooltip'),
            value: formatCurrency(qualifiedProfit, {
              currency: 'USD',
              decimalPlaces: isTabletOrMobile ? 0 : 2,
            }),
            numericValue: qualifiedProfit,
            valueProps,
            compact,
            minWidth: isTabletOrMobile ? 100 : undefined,
          },
        ]
      : []),
    {
      label: t('metrics.target.label'),
      tooltip: t('metrics.target.tooltip'),
      value: formatCurrency(target, {
        currency: 'USD',
        decimalPlaces: isTabletOrMobile ? 0 : 2,
      }),
      numericValue: target,
      valueProps,
      compact,
      minWidth: isTabletOrMobile ? 100 : undefined,
    },
  ];

  return (
    <Stack
      direction="row"
      flex={1}
      mt={4}
      gap={3}
      alignItems="flex-end"
      flexWrap={isTabletOrMobile ? 'wrap' : 'nowrap'}
    >
      {metrics.map((metric, index) => (
        <React.Fragment key={index}>
          <Metric {...metric} />
          {!compact && !isTabletOrMobile && index < metrics.length - 1 && (
            <MetricSeparator compact={compact} />
          )}
        </React.Fragment>
      ))}
      {extra && (
        <Box
          sx={{
            display: { xs: 'flex', lg: 'none', xl: 'flex' },
            alignItems: 'flex-end',
          }}
        >
          {extra}
        </Box>
      )}
    </Stack>
  );
};
